import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, bookUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import {
  Search as SearchIcon,
  BookOpen,
  Library,
  Sparkles,
  Bookmark,
  X,
  ChevronRight,
} from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { numberToHebrewLetter } from "../lib/hebrewNumerals";
import { talmudDafLabel } from "../lib/hebrewRef";

/**
 * Left navigation panel — desktop-only.
 *
 * Three sections: Search, Jump-to-chapter, Bookmarks.
 * All items are keyboard-focusable and the search input is exposed via a
 * ref so the Reader can focus it with Ctrl/Cmd+F.
 *
 * Fully bilingual: Hebrew copy is preserved byte-identically via he.json
 * key parity (`nav_panel.*` namespace). English UI shows EN book names
 * (from Sefaria's `book.en` / `book_he`-vs-`book` switch) and arabic
 * numerals for chapters so the panel reads naturally with the rest of
 * the EN chrome.
 */
export default function ReaderLeftPanel({
  book,
  slug,
  currentChapter,
  searchInputRef,
  onClose,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const [query, setQuery] = useState("");
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    if (!user) {
      setBookmarks([]);
      return;
    }
    api
      .bookmarks()
      .then((r) => setBookmarks(r.bookmarks || []))
      .catch(() => {});
  }, [user]);

  const onSearchKey = useCallback(
    (e) => {
      if (e.key === "Enter" && query.trim().length > 0) {
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      }
    },
    [query, navigate]
  );

  const total = book?.chapters || 0;
  // Talmud books use sequential amudim ("2a"/"2b") instead of integer
  // chapters, so the navigator grid must render their daf+amud label
  // and use a wider cell to fit it ("דף ב׳ ע״א" → ~6 chars). Tanakh /
  // Mishnah stays on the original square grid — zero behaviour change.
  const isTalmud = book?.kind === "talmud";

  // Chapter cell label. In EN mode we use arabic numerals for non-talmud
  // books so they line up with the rest of the EN chrome. Hebrew mode
  // keeps the canonical gematria letter ("א", "ב", …).
  const chapterDisplay = (n) => {
    if (isTalmud) return talmudDafLabel(n);
    return isEn ? String(n) : numberToHebrewLetter(n);
  };
  const chapterCountDisplay = isTalmud
    ? `${currentChapter} / ${total}`
    : isEn
    ? `${currentChapter} / ${total}`
    : `${numberToHebrewLetter(currentChapter)} / ${numberToHebrewLetter(total)}`;

  // Title at the top of the panel — show the EN book name in EN mode,
  // HE in HE mode. Both fall back to the other when missing so the
  // panel never shows the literal string "undefined".
  const bookTitle = isEn
    ? (book?.en || book?.he || t("nav_panel.loading"))
    : (book?.he || book?.en || t("nav_panel.loading"));

  return (
    <aside
      dir={isEn ? "ltr" : "rtl"}
      data-testid="reader-left-panel"
      className="flex flex-col h-full min-h-0 w-full bg-card/50 border-e border-border/60"
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="space-y-0.5">
          <p className="font-latin italic text-[11px] tracking-[0.35em] uppercase text-gold">
            Navigator · נווט
          </p>
          <h3 className="font-heading text-xl text-foreground">
            {bookTitle}
          </h3>
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={t("nav_panel.close_aria")}
            data-testid="left-panel-close"
            className="text-muted-foreground hover:text-gold hover:bg-gold/10 flex items-center gap-1.5"
          >
            <X className="h-5 w-5" strokeWidth={2} />
            <span className="text-sm font-heading">{t("nav_panel.close")}</span>
          </Button>
        )}
      </div>
      <div className="mx-5 h-px bg-gradient-to-l from-transparent via-gold/40 to-transparent" />

      {/* Search */}
      <div className="px-5 py-4">
        <label className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5 font-heading tracking-wider">
          <SearchIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("nav_panel.search_label")}
          <span className={`${isEn ? "mr-auto" : "ml-auto"} font-latin text-[10px] opacity-60`}>
            Ctrl+F
          </span>
        </label>
        <Input
          ref={searchInputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKey}
          placeholder={t("nav_panel.search_placeholder")}
          dir={isEn ? "ltr" : "rtl"}
          data-testid="reader-left-search-input"
          className="bg-background border-border/60 focus-visible:ring-gold"
        />
      </div>
      <div className="mx-5 h-px bg-border/50" />

      {/* Chapter grid */}
      <div className="px-5 py-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 font-heading tracking-wider">
          <BookOpen className="h-3.5 w-3.5" strokeWidth={1.5} />
          {isTalmud ? t("nav_panel.dapim") : t("nav_panel.chapters")}
          <span className={`${isEn ? "mr-auto" : "ml-auto"} font-latin text-[10px] opacity-60`}>
            {chapterCountDisplay}
          </span>
        </div>
        <div
          className={`grid ${isTalmud ? "grid-cols-4" : "grid-cols-7"} gap-1.5 ${isTalmud ? "talmud-daf-grid" : ""}`}
          data-testid="reader-left-chapter-grid"
          style={isTalmud ? {
            maxHeight: "min(60vh, 380px)",
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            paddingInlineEnd: "4px",
          } : undefined}
        >
          {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
            <Link
              key={n}
              to={`/reader/${bookUrl(slug)}/${n}`}
              data-testid={`left-chapter-${n}`}
              className={`h-8 grid place-items-center rounded-sm border font-heading text-[13px] transition-all
                ${
                  n === currentChapter
                    ? "border-gold bg-gold/20 text-gold"
                    : "border-border/50 text-foreground hover:border-gold/60 hover:bg-gold/5"
                }`}
              title={
                isTalmud
                  ? chapterDisplay(n)
                  : isEn
                  ? `Chapter ${n}`
                  : `פרק ${numberToHebrewLetter(n)}`
              }
            >
              {chapterDisplay(n)}
            </Link>
          ))}
        </div>
      </div>
      <div className="mx-5 h-px bg-border/50" />

      {/* Bookmarks */}
      <div className="px-5 py-4 flex-1 min-h-0 overflow-y-auto">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 font-heading tracking-wider">
          <Bookmark className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("nav_panel.my_bookmarks")}
          <span className={`${isEn ? "mr-auto" : "ml-auto"} font-latin text-[10px] opacity-60`}>
            {bookmarks.length}
          </span>
        </div>
        {!user && (
          <p className="text-xs text-muted-foreground mt-1">
            {t("nav_panel.bookmarks_need_auth")}
          </p>
        )}
        {user && bookmarks.length === 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            {t("nav_panel.no_bookmarks")}
          </p>
        )}
        <ul className="space-y-1.5 mt-1">
          {bookmarks.slice(0, 50).map((b) => (
            <li key={`${b.book}-${b.chapter}-${b.verse}`}>
              <Link
                to={`/reader/${bookUrl(b.book)}/${b.chapter}?v=${b.verse}`}
                data-testid={`left-bookmark-${b.book}-${b.chapter}-${b.verse}`}
                className="group flex items-center justify-between gap-2 text-[13px] font-heading text-foreground hover:text-gold border border-transparent hover:border-gold/30 hover:bg-gold/5 rounded-sm px-2 py-1.5 transition-all"
              >
                <span className="truncate">
                  {/* EN reference style: "Genesis 1:1" with arabic
                      numerals; HE keeps gematria "בראשית א׳:א׳". */}
                  {isEn
                    ? `${b.book || b.book_he} ${b.chapter}:${b.verse}`
                    : `${b.book_he || b.book} ${numberToHebrewLetter(b.chapter)}:${numberToHebrewLetter(b.verse)}`}
                </span>
                <ChevronRight
                  className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity rtl:rotate-180"
                  strokeWidth={1.5}
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer links */}
      <div className="mx-5 h-px bg-border/50" />
      <div className="px-5 py-3 flex items-center gap-1 text-xs">
        <Link
          to="/library"
          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-gold transition-colors font-heading"
          data-testid="left-panel-library-link"
        >
          <Library className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("nav_panel.library_link")}
        </Link>
        <span className="text-border/60">·</span>
        <Link
          to="/daily"
          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-gold transition-colors font-heading"
          data-testid="left-panel-daily-link"
        >
          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("nav_panel.daily_verse_link")}
        </Link>
      </div>
    </aside>
  );
}
